/**
 * Registre de scoring — chunk 9.AJ.1.
 *
 * ## Ce que ce fichier déclare, et pourquoi il le déclare plutôt qu'il ne le devine
 *
 * Pour chaque critère : **le sens** (plus haut vaut mieux, ou plus bas), et le
 * **poids par défaut**. Le sens n'est PAS dérivable :
 *
 *   `dma.coverage` est en `$`, et plus il est haut, mieux c'est.
 *   `prime_mensuelle_total` est en `$`, et plus elle est basse, mieux c'est.
 *   `amc.deductible` est en `$`, et plus elle est basse, mieux c'est.
 *
 * Trois champs, la même unité, deux sens opposés. Aucune convention de nommage ne
 * tranche ça de façon sûre, et se tromper de sens **inverse silencieusement un
 * classement** — le défaut le plus coûteux que ce chunk puisse produire.
 *
 * Le sens est donc de la **connaissance métier**, pas une préférence de cabinet : un
 * cabinet ne décide pas qu'une prime élevée est meilleure. Il vit ici, en dur et
 * relu ; ce qui se configure par cabinet, ce sont les **poids** et l'activation.
 *
 * ## Ce qui n'est pas déclaré n'est pas scoré
 *
 * Un champ absent de ce registre n'est jamais scoré « au jugé ». Il est **exclu et
 * signalé**. C'est la même posture que `readableStatus()` : une sémantique qu'on ne
 * comprend pas se lit « à vérifier », jamais « offert ». Un critère dont on ignore
 * le sens ne peut pas départager deux assureurs.
 *
 * ## Le poids par défaut est NEUTRE, et c'est un choix assumé
 *
 * Tous les critères déclarés pèsent `1`. Conséquence à connaître : les garanties
 * sont nombreuses (19 critères) et les primes peu (2), donc à poids égal le score
 * par défaut parle surtout de **couverture**, pas de **coût**. Ce n'est pas un
 * réglage caché — c'est précisément ce que les poids par cabinet servent à corriger,
 * et l'interface montre la contribution de chaque critère pour que ce soit visible.
 */

import { guaranteeFamilyOf } from "./field-registry.js";

/** Le sens d'un critère. Connaissance métier, jamais une préférence de cabinet. */
export const SCORE_DIRECTIONS = ["higher_is_better", "lower_is_better"] as const;
export type ScoreDirection = (typeof SCORE_DIRECTIONS)[number];

export type CriterionDeclaration = {
  field_key: string;
  direction: ScoreDirection;
  /** Poids par défaut. Neutre à `1` — cf. l'en-tête. */
  default_weight: number;
};

/**
 * Les critères dont le sens est établi, dérivés des champs déclarés par
 * `EXTRACTION_PASSES`. Un champ du contrat qui n'apparaît pas ici est un champ
 * qu'on refuse de scorer, et la raison est toujours l'une des trois :
 *
 *   - il n'est pas numériquement ordonnable (`exclusions`, `general_notes`,
 *     `ic_long_terme.definition`, `prime_par_garantie`) ;
 *   - c'est une métadonnée de document (`insurer_code`, `document_type`,
 *     `language`, `effect_date`) — elle décrit le PDF, pas l'offre ;
 *   - son sens est discutable et personne ne l'a tranché.
 */
const DECLARATIONS: readonly CriterionDeclaration[] = [
  // --- Coût et délais : plus bas vaut mieux -----------------------------------
  // Une prime, une franchise, un délai de carence : ce que l'employeur paie ou
  // attend. Personne ne préfère en avoir davantage.
  { field_key: "prime_mensuelle_total", direction: "lower_is_better", default_weight: 1 },
  { field_key: "prime_annuelle_total", direction: "lower_is_better", default_weight: 1 },
  { field_key: "amc.deductible", direction: "lower_is_better", default_weight: 1 },
  { field_key: "vie_base.elimination", direction: "lower_is_better", default_weight: 1 },
  { field_key: "ic_court_terme.elimination", direction: "lower_is_better", default_weight: 1 },
  { field_key: "ic_long_terme.elimination", direction: "lower_is_better", default_weight: 1 },
  { field_key: "waiting_period_days", direction: "lower_is_better", default_weight: 1 },
  // Heures minimales pour être admissible : moins d'heures = plus d'employés
  // couverts. C'est une restriction, pas une garantie.
  { field_key: "admissibility_hours", direction: "lower_is_better", default_weight: 1 },

  // --- Couverture : plus haut vaut mieux --------------------------------------
  { field_key: "vie_base.salary_multiple", direction: "higher_is_better", default_weight: 1 },
  { field_key: "vie_base.max_amount", direction: "higher_is_better", default_weight: 1 },
  // Âge auquel la couverture commence à décroître : plus tard, mieux c'est.
  { field_key: "vie_base.reduction_age", direction: "higher_is_better", default_weight: 1 },
  { field_key: "dma.coverage", direction: "higher_is_better", default_weight: 1 },
  { field_key: "dma.max_amount", direction: "higher_is_better", default_weight: 1 },
  { field_key: "amc.coinsurance", direction: "higher_is_better", default_weight: 1 },
  { field_key: "amc.max_annual", direction: "higher_is_better", default_weight: 1 },
  { field_key: "amc.medicaments_coinsurance", direction: "higher_is_better", default_weight: 1 },
  { field_key: "amc.paramedical_max", direction: "higher_is_better", default_weight: 1 },
  { field_key: "amc.travel_max", direction: "higher_is_better", default_weight: 1 },
  { field_key: "dentaire.preventif_coinsurance", direction: "higher_is_better", default_weight: 1 },
  { field_key: "dentaire.basic_coinsurance", direction: "higher_is_better", default_weight: 1 },
  { field_key: "dentaire.major_coinsurance", direction: "higher_is_better", default_weight: 1 },
  { field_key: "dentaire.ortho_coinsurance", direction: "higher_is_better", default_weight: 1 },
  { field_key: "dentaire.max_annual", direction: "higher_is_better", default_weight: 1 },
  // Année du guide de tarifs : plus récent = remboursements alignés sur les tarifs
  // courants. Un guide de 2019 rembourse aux prix de 2019.
  { field_key: "dentaire.fee_guide_year", direction: "higher_is_better", default_weight: 1 },
  { field_key: "ic_court_terme.percentage", direction: "higher_is_better", default_weight: 1 },
  { field_key: "ic_court_terme.max_weekly", direction: "higher_is_better", default_weight: 1 },
  { field_key: "ic_court_terme.duration", direction: "higher_is_better", default_weight: 1 },
  { field_key: "ic_long_terme.percentage", direction: "higher_is_better", default_weight: 1 },
  { field_key: "ic_long_terme.max_monthly", direction: "higher_is_better", default_weight: 1 },

  // --- Échelle ordonnée reconnue ----------------------------------------------
  // `amc.hospital_room` n'est pas un nombre : c'est une échelle publique <
  // semi-privée < privée, déjà normalisée en 1|2|3 par `hospitalRoomLevel()`
  // (9.AB.1) et testée là-bas. On la réutilise plutôt que d'en réinventer une —
  // deux échelles finiraient par se contredire. Une valeur non reconnue rend
  // `null` et le critère devient non scorable, ce qui est le comportement voulu.
  { field_key: "amc.hospital_room", direction: "higher_is_better", default_weight: 1 },
];

const PAR_CHAMP: ReadonlyMap<string, CriterionDeclaration> = new Map(
  DECLARATIONS.map((d) => [d.field_key, d]),
);

/** La déclaration d'un critère, ou `null` s'il n'est pas scorable par décision. */
export function criterionDeclaration(fieldKey: string): CriterionDeclaration | null {
  return PAR_CHAMP.get(fieldKey) ?? null;
}

/** Tous les critères déclarés, ordre stable, avec leur famille de garantie. */
export const SCORABLE_CRITERIA: readonly (CriterionDeclaration & {
  guarantee_family: string | null;
})[] = DECLARATIONS.map((d) => ({
  ...d,
  guarantee_family: guaranteeFamilyOf(d.field_key),
}));
